/**
 * Formatting helpers. The API prices markets as a 0–1 probability plus an
 * `american_odds` string. Per the design brief we lead with cents (venue-native
 * for Kalshi/Polymarket) and show American as dimmer subtext. Everything renders
 * in tabular-nums so columns don't jitter.
 */

export type Nullable<T> = T | null | undefined;

const DASH = "—";

/* ------------------------------------------------------------------- money */

const USD_FMT = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const USD_WHOLE = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/** `81373.23 → "$81,373.23"`. Pass `whole` to drop cents (hero figures). */
export function fmtMoney(n: Nullable<number>, opts?: { whole?: boolean }): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return DASH;
  return (opts?.whole ? USD_WHOLE : USD_FMT).format(n);
}

/**
 * Money in an arbitrary currency using its symbol + precision (from the balances
 * payload). Play-money currencies like ATLAS have no ISO code, so we format the
 * number and prefix the symbol/label ourselves.
 */
export function fmtCurrency(
  n: Nullable<number>,
  currency?: Nullable<string>,
  symbol?: Nullable<string>,
  precision?: Nullable<number>,
): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return DASH;
  if (currency === "USD") return fmtMoney(n);
  const digits = precision ?? 2;
  const num = n.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  if (symbol && symbol !== "$") return `${num} ${symbol}`;
  if (symbol === "$") return `$${num}`;
  return currency ? `${num} ${currency}` : num;
}

/** Signed money for P&L: `+$12.40` / `−$9.92`. Uses a real minus glyph. */
export function fmtSignedMoney(n: Nullable<number>): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return DASH;
  const sign = n > 0 ? "+" : n < 0 ? "−" : "";
  return `${sign}${fmtMoney(Math.abs(n))}`;
}

/** Tailwind text-color token for a P&L value (muted at/near zero). */
export function pnlTone(n: Nullable<number>): "text-up" | "text-down" | "text-dim" {
  if (n === null || n === undefined || !Number.isFinite(n) || Math.abs(n) < 0.005) return "text-dim";
  return n > 0 ? "text-up" : "text-down";
}

/** ROI as a signed percent, e.g. 0.042 → "+4.2%". Input is a 0–1 fraction. */
export function fmtRoi(n: Nullable<number>): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return DASH;
  const pct = n * 100;
  const sign = pct > 0 ? "+" : pct < 0 ? "−" : "";
  return `${sign}${Math.abs(pct).toFixed(1)}%`;
}

/* ------------------------------------------------------ odds-format dispatch */

export type OddsFormat = "cents" | "percent" | "american";

/**
 * Render a market price in the user's selected odds format. American prefers the
 * API-provided string (it encodes vig correctly); when absent we derive it from
 * the probability so the toggle still works everywhere.
 */
export function fmtPrice(
  price: Nullable<number>,
  american: Nullable<string>,
  format: OddsFormat,
): string {
  if (price === null || price === undefined || !Number.isFinite(price)) {
    return format === "american" ? fmtAmerican(american) : DASH;
  }
  switch (format) {
    case "percent":
      return fmtPercent(price);
    case "american":
      return american && american.trim() ? american : deriveAmerican(price);
    case "cents":
    default:
      return fmtCents(price);
  }
}

/** Probability → American odds string when the API didn't supply one. */
export function deriveAmerican(price: number): string {
  if (!Number.isFinite(price) || price <= 0 || price >= 1) return DASH;
  const odds = price >= 0.5 ? -(price / (1 - price)) * 100 : ((1 - price) / price) * 100;
  const rounded = Math.round(odds);
  return rounded > 0 ? `+${rounded}` : String(rounded);
}

/** 0–1 probability → cents string, e.g. 0.673 → "67.3¢". Sub-cent precision kept. */
export function fmtCents(price: Nullable<number>): string {
  if (price === null || price === undefined || !Number.isFinite(price)) return DASH;
  const cents = price * 100;
  // One decimal of sub-cent precision, but drop a trailing ".0" for whole cents.
  const rounded = Math.round(cents * 10) / 10;
  const s = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return `${s}¢`;
}

/** Implied probability as a percent, e.g. 0.673 → "67.3%". */
export function fmtPercent(price: Nullable<number>): string {
  if (price === null || price === undefined || !Number.isFinite(price)) return DASH;
  return `${(price * 100).toFixed(1)}%`;
}

/** American odds passthrough (already a signed string from the API). */
export function fmtAmerican(odds: Nullable<string>): string {
  return odds && odds.trim() ? odds : DASH;
}

/** Signed cent delta of a price vs consensus, e.g. +3.2 (in cents). */
export function centDelta(price: Nullable<number>, consensus: Nullable<number>): number | null {
  if (
    price === null ||
    price === undefined ||
    consensus === null ||
    consensus === undefined ||
    !Number.isFinite(price) ||
    !Number.isFinite(consensus)
  ) {
    return null;
  }
  return (price - consensus) * 100;
}

/** Render a cent delta with an explicit sign and a minus glyph, e.g. "+3.2¢". */
export function fmtCentDelta(delta: number | null): string {
  if (delta === null) return DASH;
  const rounded = Math.round(delta * 10) / 10;
  if (rounded === 0) return "0¢";
  const sign = rounded > 0 ? "+" : "−";
  return `${sign}${Math.abs(rounded).toFixed(1)}¢`;
}

/** Compact available-size, e.g. 266542 → "266.5k", 1_200_000 → "1.2M". */
export function fmtSize(available: Nullable<number>): string {
  if (available === null || available === undefined || !Number.isFinite(available)) return DASH;
  const n = available;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  if (n >= 100) return String(Math.round(n));
  return n.toFixed(n < 10 ? 1 : 0);
}

/** Kickoff / start time: compact local date + time, e.g. "Jun 11, 8:00p". */
export function fmtKickoff(iso: Nullable<string>): string {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return DASH;
  const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const time = d
    .toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
    .replace(/\s?([AP])M/i, (_m, p: string) => p.toLowerCase());
  return `${date}, ${time}`;
}

/** Rough "in 3h" / "2d ago" relative label for a start time. */
export function fmtRelative(iso: Nullable<string>): string {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return DASH;
  const diffMs = d.getTime() - Date.now();
  const abs = Math.abs(diffMs);
  const mins = Math.round(abs / 60000);
  const hours = Math.round(abs / 3_600_000);
  const days = Math.round(abs / 86_400_000);
  let unit: string;
  if (mins < 60) unit = `${mins}m`;
  else if (hours < 48) unit = `${hours}h`;
  else unit = `${days}d`;
  return diffMs >= 0 ? `in ${unit}` : `${unit} ago`;
}

/** Title-case a snake/kebab key, e.g. "team_tournament_winner" → "Team Tournament Winner". */
export function humanizeKey(key: Nullable<string>): string {
  if (!key) return "";
  return key
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

/** Absolute local timestamp, e.g. "Jul 7, 2:14:58p" — for blotter/activity rows. */
export function fmtDateTime(iso: Nullable<string>): string {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return DASH;
  const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const time = d
    .toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
    .replace(/\s?([AP])M/i, (_m, p: string) => p.toLowerCase());
  return `${date}, ${time}`;
}

/** Compact "age" of a timestamp: "3m", "2h", "5d", "just now". */
export function fmtAge(iso: Nullable<string>): string {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return DASH;
  const ms = Date.now() - d.getTime();
  if (ms < 60_000) return "just now";
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(ms / 3_600_000);
  if (hours < 48) return `${hours}h`;
  const days = Math.round(ms / 86_400_000);
  return `${days}d`;
}

/**
 * Deterministic hue (0–360) from a venue name, for the monogram-chip fallback when
 * a partner has no `logo_url`. Stable per name so a venue always keeps its color.
 */
export function venueHue(name: Nullable<string>): number {
  const s = name ?? "";
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return h;
}

/** Two-letter monogram from a venue name, e.g. "PolymarketUS" → "PO". */
export function venueMonogram(name: Nullable<string>): string {
  const s = (name ?? "").trim();
  if (!s) return "··";
  const parts = s.split(/[\s_-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}
